import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'
import { toast } from 'react-toastify'
import { amenityDisplayName } from '../../Utils/amenityDisplayName'
import {
    linkAmenityService,
    listAmenityCatalogService,
    listMyPlaceAmenitiesService,
    unlinkAmenityService,
} from '../../Service/AmenityService'

function ReligiousPlaceAmenitiesComponent({ user_access_key }) {
    const { t, i18n } = useTranslation('user_home')
    const [catalog, setCatalog] = useState([])
    const [linked, setLinked] = useState([])
    const [selectedId, setSelectedId] = useState(null)
    const [loading, setLoading] = useState(false)

    const reload = useCallback(async () => {
        if (!user_access_key) return
        const [cat, mine] = await Promise.all([
            listAmenityCatalogService(),
            listMyPlaceAmenitiesService(user_access_key),
        ])
        if (Array.isArray(cat)) setCatalog(cat)
        if (Array.isArray(mine)) setLinked(mine)
    }, [user_access_key])

    useEffect(() => {
        reload()
    }, [reload])

    const linkedIds = useMemo(() => new Set(linked.map((a) => a.id)), [linked])

    const addOptions = useMemo(
        () =>
            catalog
                .filter((a) => !linkedIds.has(a.id))
                .map((a) => ({
                    label: amenityDisplayName(a, i18n.language),
                    value: a.id,
                })),
        [catalog, linkedIds, i18n.language]
    )

    const handleLink = async () => {
        if (!selectedId || !user_access_key) return
        setLoading(true)
        const ok = await linkAmenityService(user_access_key, selectedId)
        setLoading(false)
        if (ok) {
            toast.success(t('user_home_amenities_linked'))
            setSelectedId(null)
            reload()
        } else {
            toast.error(t('user_home_amenities_error'))
        }
    }

    const handleUnlink = (amenity) => {
        confirmDialog({
            message: `${t('user_home_amenities_unlink_message')}${amenityDisplayName(amenity, i18n.language)}?`,
            header: t('user_home_amenities_unlink_confirmation'),
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                const ok = await unlinkAmenityService(user_access_key, amenity.id)
                if (ok) {
                    toast.success(t('user_home_amenities_unlinked'))
                    reload()
                } else {
                    toast.error(t('user_home_amenities_error'))
                }
            },
            reject: () => {},
        })
    }

    return (
        <div className='flex flex-column gap-3'>
            <ConfirmDialog />
            <div className='flex justify-content-between align-items-end gap-2 flex-wrap'>
                <div>
                    <span className='font-bold'>{linked.length}</span> {t('user_home_amenities_count_linked')}
                </div>
            </div>
            <div className='flex flex-column md:flex-row gap-2 align-items-end'>
                <div className='flex flex-column gap-1 flex-1'>
                    <span className='text-sm font-bold'>{t('user_home_amenities_add_label')}</span>
                    <Dropdown
                        className='w-full'
                        value={selectedId}
                        options={addOptions}
                        onChange={(e) => setSelectedId(e.value)}
                        placeholder={t('user_home_amenities_select_placeholder')}
                        filter
                        showClear
                        disabled={addOptions.length === 0}
                    />
                </div>
                <Button
                    label={t('user_home_amenities_add_button')}
                    icon='pi pi-plus'
                    onClick={handleLink}
                    disabled={!selectedId || loading || addOptions.length === 0}
                    loading={loading}
                />
            </div>
            {addOptions.length === 0 && catalog.length > 0 && linked.length > 0 ? (
                <div className='text-600 text-sm'>{t('user_home_amenities_all_linked')}</div>
            ) : null}
            {linked.length === 0 ? (
                <div className='text-600'>{t('user_home_amenities_empty')}</div>
            ) : (
                <div className='flex flex-column gap-2'>
                    {linked.map((a) => (
                        <div key={a.id} className='flex justify-content-between align-items-center border-1 border-round p-2'>
                            <span>{amenityDisplayName(a, i18n.language)}</span>
                            <Button
                                icon='pi pi-times'
                                severity='danger'
                                text
                                rounded
                                onClick={() => handleUnlink(a)}
                                aria-label={t('user_home_amenities_remove')}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default ReligiousPlaceAmenitiesComponent
